import { useCallback, type KeyboardEvent } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import { Plus, X } from 'lucide-react';
import { AppInput } from '@/shared/components/ui/AppInput/AppInput';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import type { CreativeFormValues } from '../../../../schemas/creative.schema';
import type { PlatformCreativeConstraints } from '../../../../logic/platform-constraints';
import { type CopyField, buildFieldHelpText, getFieldMax } from './AdCopyFields.logic';
import styles from './AdCopyFields.module.scss';

interface AdCopyFieldsProps {
  /**
   * React Hook Form instance from the parent step. We bind directly instead of
   * owning local state so the step's zod schema is the single source of truth
   * for validation.
   */
  form: UseFormReturn<CreativeFormValues>;
  /** Index into `creatives` — always 0 today (the wizard edits one pooled creative). */
  creativeIndex: number;
  /** Platform limits; null until a platform is picked (mid-wizard draft). */
  constraints: PlatformCreativeConstraints | null;
  /** Live headline pool for the edited creative. */
  headlines: string[];
  /** Live description pool for the edited creative. */
  descriptions: string[];
  /** Warning toast when a platform cap is hit — the step owns the toast store. */
  onLimitReached: (message: string) => void;
}

/**
 * Ad copy editor for the Creative step.
 *
 * Renders headlines and descriptions as two independent lists rather than
 * pairing them into cards — Meta (`asset_feed_spec`) and Google (RSA/RDA) both
 * mix headlines and descriptions freely at serve time, so there is no pairing
 * to preserve in the UI. Each pool has its own add/remove and its own
 * platform-driven min/max.
 */
export function AdCopyFields({
  form,
  creativeIndex,
  constraints,
  headlines,
  descriptions,
  onLimitReached,
}: AdCopyFieldsProps) {
  const {
    register,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = form;

  const headlinesPath = `creatives.${creativeIndex}.headlines` as const;
  const descriptionsPath = `creatives.${creativeIndex}.descriptions` as const;

  const handleAdd = useCallback(
    (field: CopyField) => {
      const path = field === 'headlines' ? headlinesPath : descriptionsPath;
      const current = getValues(path) ?? [];
      const max = getFieldMax(constraints, field);
      if (current.length >= max) {
        onLimitReached(`Maximum ${max} ${field} allowed for this platform.`);
        return;
      }
      setValue(path, [...current, ''], { shouldDirty: true });
    },
    [constraints, descriptionsPath, getValues, headlinesPath, onLimitReached, setValue],
  );

  const handleRemove = useCallback(
    (field: CopyField, entryIndex: number) => {
      const path = field === 'headlines' ? headlinesPath : descriptionsPath;
      const current = getValues(path) ?? [];
      setValue(
        path,
        current.filter((_, i) => i !== entryIndex),
        { shouldDirty: true },
      );
      // RHF keys errors by index; without this a stale per-item error re-attaches
      // to whichever row shifted up.
      clearErrors(path);
    },
    [clearErrors, descriptionsPath, getValues, headlinesPath, setValue],
  );

  const creativeErrors = errors.creatives?.[creativeIndex];
  // Array-level message from the schema's superRefine ("Add at least one
  // headline"). Rendered once under the headline list rather than on any single field.
  const headlinePoolError = creativeErrors?.headlines?.message;

  /**
   * Enter adds another field of the same pool instead of submitting
   * (PLX-7338). The form-level handler in CreativeStep already blocks Enter
   * from advancing the wizard.
   */
  const handleEnterAdds = (field: CopyField) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleAdd(field);
  };

  const renderCounter = (value: string, maxLength: number | undefined) => {
    if (maxLength == null) return null;
    const charCount = value.length;
    return (
      <span className={clsx(styles.charCounter, charCount > maxLength && styles.charCounterError)}>
        {charCount}/{maxLength}
      </span>
    );
  };

  const renderFieldList = (field: CopyField, label: string) => {
    const values = field === 'headlines' ? headlines : descriptions;
    const path = field === 'headlines' ? headlinesPath : descriptionsPath;
    const max = getFieldMax(constraints, field);
    const maxLength = constraints?.[field].maxLength;
    const singular = field === 'headlines' ? 'headline' : 'description';
    const canAdd = values.length < max;
    const canRemove = values.length > 1;

    return (
      <div className={styles.fieldSection}>
        <h3 className={styles.fieldSectionTitle}>{label}</h3>
        <p className={styles.fieldSectionHint}>{buildFieldHelpText(constraints, field)}</p>

        <div className={styles.fieldList}>
          {values.map((value, index) => {
            const inputId = `creative-${singular}-${index}`;
            return (
              <div key={index} className={styles.fieldRow}>
                <div className={styles.fieldWithCounter}>
                  <AppInput
                    id={inputId}
                    aria-label={`${label.replace(/s$/, '')} ${index + 1}`}
                    placeholder={`${label.replace(/s$/, '')} ${index + 1}`}
                    data-testid={`creative-${singular}-input-${index}`}
                    {...register(`${path}.${index}` as const)}
                    onKeyDown={handleEnterAdds(field)}
                    error={
                      field === 'headlines'
                        ? creativeErrors?.headlines?.[index]?.message
                        : creativeErrors?.descriptions?.[index]?.message
                    }
                  />
                  {renderCounter(value ?? '', maxLength)}
                </div>
                <button
                  type="button"
                  className={styles.removeFieldBtn}
                  onClick={() => handleRemove(field, index)}
                  disabled={!canRemove}
                  aria-label={`Remove ${singular} ${index + 1}`}
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>

        {canAdd && (
          <AppButton type="button" variant="ghost" size="sm" onClick={() => handleAdd(field)}>
            <Plus size={14} />
            {`Add ${singular}`}
          </AppButton>
        )}

        {field === 'headlines' && headlinePoolError && (
          <p className={styles.copyError} role="alert">
            {headlinePoolError}
          </p>
        )}
      </div>
    );
  };

  return (
    <>
      {renderFieldList('headlines', 'Headlines')}
      {renderFieldList('descriptions', 'Descriptions')}
    </>
  );
}
